export type LocaleNumberMode = 'integer' | 'decimal';

/** Parsea texto con convención es-AR (miles `.`, decimal `,`). */
export function parseLocaleNumberAr(raw: string): number | null {
  const trimmed = String(raw ?? '').trim();
  if (!trimmed) return null;
  const normalized = trimmed.replace(/\./g, '').replace(',', '.');
  const n = Number(normalized);
  if (!Number.isFinite(n)) return null;
  return n;
}

export function formatLocaleNumberAr(
  value: number | null | undefined,
  mode: LocaleNumberMode,
  maxFractionDigits = 2
): string {
  if (value == null || !Number.isFinite(value)) return '';
  if (mode === 'integer') {
    return new Intl.NumberFormat('es-AR', { maximumFractionDigits: 0 }).format(Math.round(value));
  }
  return new Intl.NumberFormat('es-AR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: maxFractionDigits,
  }).format(value);
}

function sanitizeDecimalTyping(raw: string): string {
  let s = raw.replace(/[^\d,]/g, '');
  const comma = s.indexOf(',');
  if (comma >= 0) {
    s = s.slice(0, comma + 1) + s.slice(comma + 1).replace(/,/g, '');
  }
  return s;
}

function formatIntegerWhileTyping(raw: string): { text: string; value: number | null } {
  const digits = raw.replace(/\D/g, '');
  if (!digits) return { text: '', value: null };
  const n = Number(digits);
  if (!Number.isFinite(n)) return { text: '', value: null };
  return { text: formatLocaleNumberAr(n, 'integer'), value: n };
}

function formatDecimalWhileTyping(raw: string): { text: string; value: number | null } {
  const s = sanitizeDecimalTyping(raw);
  if (!s) return { text: '', value: null };
  if (s.endsWith(',')) {
    const head = s.slice(0, -1).replace(/\./g, '');
    if (!head) return { text: '0,', value: 0 };
    const n = Number(head);
    if (!Number.isFinite(n)) return { text: s, value: null };
    return { text: formatLocaleNumberAr(n, 'integer') + ',', value: n };
  }
  const n = parseLocaleNumberAr(s);
  if (n == null) return { text: s, value: null };
  return { text: formatLocaleNumberAr(n, 'decimal', 4), value: n };
}

export interface LocaleNumberInputOptions {
  mode: LocaleNumberMode;
  maxFractionDigits?: number;
  onValueChange?: (value: number | null) => void;
}

export interface LocaleNumberInputHandle {
  getValue(): number | null;
  setValue(value: number | null): void;
  clear(): void;
}

export function attachLocaleNumberInput(
  input: HTMLInputElement,
  options: LocaleNumberInputOptions
): LocaleNumberInputHandle {
  input.type = 'text';
  input.classList.add('field__control', 'locale-number-input');
  input.setAttribute('inputmode', options.mode === 'integer' ? 'numeric' : 'decimal');
  input.autocomplete = 'off';
  input.spellcheck = false;

  let lastValue: number | null = null;

  const emit = (value: number | null) => {
    lastValue = value;
    options.onValueChange?.(value);
  };

  const applyDisplay = (value: number | null, editing: boolean) => {
    if (value == null) {
      input.value = '';
      return;
    }
    if (editing && options.mode === 'integer') {
      input.value = String(Math.round(value));
      return;
    }
    if (editing && options.mode === 'decimal') {
      const s = String(value).replace('.', ',');
      input.value = s;
      return;
    }
    input.value = formatLocaleNumberAr(value, options.mode, options.maxFractionDigits ?? 2);
  };

  input.addEventListener('input', () => {
    const row =
      options.mode === 'integer'
        ? formatIntegerWhileTyping(input.value)
        : formatDecimalWhileTyping(input.value);
    input.value = row.text;
    emit(row.value);
  });

  input.addEventListener('blur', () => {
    const parsed = parseLocaleNumberAr(input.value);
    emit(parsed);
    applyDisplay(parsed, false);
  });

  input.addEventListener('focus', () => {
    applyDisplay(lastValue ?? parseLocaleNumberAr(input.value), true);
    queueMicrotask(() => {
      const len = input.value.length;
      try {
        input.setSelectionRange(len, len);
      } catch {
        /* ignore */
      }
    });
  });

  return {
    getValue(): number | null {
      return parseLocaleNumberAr(input.value) ?? lastValue;
    },
    setValue(value: number | null): void {
      emit(value);
      applyDisplay(value, false);
    },
    clear(): void {
      emit(null);
      input.value = '';
    },
  };
}

export function readLocaleNumberField(id: string): number | null {
  const el = document.getElementById(id) as HTMLInputElement | null;
  if (!el) return null;
  return parseLocaleNumberAr(el.value);
}

export function clearLocaleNumberField(id: string): void {
  const el = document.getElementById(id) as HTMLInputElement | null;
  if (el) el.value = '';
}

export interface LocaleNumberFieldSpec {
  id: string;
  mode: LocaleNumberMode;
  maxFractionDigits?: number;
}

const handles = new Map<string, LocaleNumberInputHandle>();

export function initLocaleNumberFields(specs: LocaleNumberFieldSpec[]): void {
  for (const spec of specs) {
    if (handles.has(spec.id)) continue;
    const el = document.getElementById(spec.id) as HTMLInputElement | null;
    if (!el) continue;
    handles.set(
      spec.id,
      attachLocaleNumberInput(el, {
        mode: spec.mode,
        maxFractionDigits: spec.maxFractionDigits,
      })
    );
  }
}

export function clearVentaNumericFields(): void {
  clearLocaleNumberField('kilos');
  clearLocaleNumberField('precio');
  handles.get('kilos')?.clear();
  handles.get('precio')?.clear();
}
